import { useEffect, useState, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import { useStripe } from "@stripe/react-stripe-js";
import {
  refreshStripeOnboardingUrl,
  addExternalAccount,
  readExternalAccount,
  generateFinancialConnectionToken,
  updatePayoutUser,
} from "../settings/actions/index";
import { requestAuthLink, setLoginUserFromCookie } from "../login/actions";
import ProgressStepper from "../../components/ProgressStepper";
import EmailVerification from "./components/EmailVerificationStep";
import StripeOnboardingStep from "./components/StripeOnboardingStep";
import FinancialAccountStep from "./components/FinancialAccountStep";
import AlertBanner from "../../components/AlertBanner";
import AgreementsStep from "./components/AgreementsStep";
import CloseAccountConfigStep from "./components/CloseAccountConfigStep";

const stageDict = {
  verify: 0,
  onboarding: 1,
  externalAccount: 2,
  agreements: 3,
  // handle: 4,
  // phone: 4,
};

function AccountCompleteSetup({
  loginUser,
  loading,
  stripeOnboardingUrl,
  externalAccount,
  stripeFinancialToken,
  shouldUpdateUser,
  showConfirmation,

  refreshStripeOnboardingUrl,
  addExternalAccount,
  readExternalAccount,
  generateFinancialConnectionToken,
  updatePayoutUser,
  requestAuthLink,
  setLoginUserFromCookie,

  ...props
}) {
  const startAtStage = useMemo(
    () => checkRequiredUserConfig(loginUser),
    [loginUser],
  );

  const [showSection, setShowSection] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [nextButtonType, setNextButtonType] = useState("next");
  const [pendingOpenModal, setPendingOpenModal] = useState(false);
  const stripe = useStripe();

  const openModalWithToken = useCallback(
    (token) => {
      setPendingOpenModal(false);
      stripe.collectBankAccountToken({ clientSecret: token }).then((result) => {
        if (result?.token?.id) {
          const token = result.token.id;
          if (token) addExternalAccount(token);
        } else {
          // TODO: @error
        }
      });
    },
    [addExternalAccount, stripe],
  );

  useEffect(() => {
    if (startAtStage) {
      setShowSection(true);
      setShowConfig(true);
    }
  }, [startAtStage]);

  const closeConfiguration = useCallback(() => {
    setShowConfig(false);
    if (!checkRequiredUserConfig(loginUser)) {
      setShowSection(false);
    }
  }, [loginUser, setShowSection, setShowConfig]);

  useEffect(() => {
    refreshStripeOnboardingUrl();
  }, [loginUser.needsStripeOnboarding, refreshStripeOnboardingUrl]);

  useEffect(() => {
    if (stripeFinancialToken && pendingOpenModal) {
      openModalWithToken(stripeFinancialToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeFinancialToken, openModalWithToken]);

  useEffect(() => {
    if (!loginUser.needsStripeFinancialConnection) {
      readExternalAccount();
    }
  }, [loginUser.needsStripeFinancialConnection, readExternalAccount]);

  useEffect(() => {
    if (shouldUpdateUser) {
      setLoginUserFromCookie();
    }
  }, [shouldUpdateUser, setLoginUserFromCookie]);

  const initiateFinancialConnectionsModal = useCallback(async() => {
    setPendingOpenModal(true);
    await generateFinancialConnectionToken();
  }, [generateFinancialConnectionToken]);

  const steps = useMemo(
    () => [
      {
        name: "Email Verification",
        component: (
          <EmailVerification
            loginUser={loginUser}
            requestAuthLink={requestAuthLink}
            setNextButtonType={setNextButtonType}
          />
        ),
      },
      {
        name: "Stripe Onboarding",
        component: (
          <StripeOnboardingStep
            loginUser={loginUser}
            stripeOnboardingUrl={stripeOnboardingUrl}
            setNextButtonType={setNextButtonType}
          />
        ),
      },
      {
        name: "Payout Account Link",
        component: (
          <FinancialAccountStep
            loginUser={loginUser}
            initiateFinancialConnectionsModal={
              initiateFinancialConnectionsModal
            }
            setNextButtonType={setNextButtonType}
            externalAccount={externalAccount}
            loading={loading}
          />
        ),
      },
      {
        name: "Agreements",
        component: (
          <AgreementsStep
            loginUser={loginUser}
            setNextButtonType={setNextButtonType}
            showConfirmation={showConfirmation}
            closeConfiguration={closeConfiguration}
            refreshUser={setLoginUserFromCookie}
          />
        ),
      },
      // {
      //   name: "Wrapping Up",
      //   component: (
      //     <AdditionalInfoStep
      //       loginUser={loginUser}
      //       setNextButtonType={setNextButtonType}
      //       updatePayoutUser={updatePayoutUser}
      //       showConfirmation={showConfirmation}
      //       closeConfiguration={closeConfiguration}
      //     />
      //   ),
      // },
      {
        name: "",
        component: (
          <CloseAccountConfigStep closeConfiguration={closeConfiguration} />
        ),
      },
    ],
    [
      loginUser,
      stripeOnboardingUrl,
      externalAccount,
      loading,
      showConfirmation,
      requestAuthLink,
      initiateFinancialConnectionsModal,
      closeConfiguration,
      setLoginUserFromCookie,
    ],
  );

  const openConfigButton = (
    <Button
      onClick={() => setShowConfig(true)}
      className="btn btn-default btn-warning my-2"
    >
      Open Configuration
    </Button>
  );

  const buttonConfig = useMemo(
    () => buttonConfigForType(nextButtonType),
    [nextButtonType],
  );

  const progressStepperProps = useMemo(
    () => ({
      startAtStep: startAtStage ? stageDict[startAtStage] : 0,
      showNavigation: true,
      steps,
      nextButtonText: buttonConfig.text,
      nextButtonCls: buttonConfig.class,
    }),
    [startAtStage, steps, buttonConfig.text, buttonConfig.class],
  );

  return (
    <>
      {showSection && (
        <>
          {showConfig ? (
            <Row>
              <Col xs="12">
                <h1 className="heading title">Account Configuration</h1>
                <AlertBanner message="Payouts are disabled. Complete all required account configuration steps to enable payouts." />
              </Col>
              <Col xs="12">
                <Card className="card-custom">
                  <CardBody>
                    <ProgressStepper {...progressStepperProps} />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs="12">
                <h1 className="heading title">Account Configuration</h1>
                <AlertBanner
                  message={
                    "Payouts are disabled. Complete all required account configuration steps to enable payouts."
                  }
                  button={openConfigButton}
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
}

function buttonConfigForType(nextButtonType = "next") {
  switch (nextButtonType) {
    case "save":
      return {
        text: "Save",
        class: "btn btn-primary float-right btn-stepper stepper-nav-btn",
      };
    case "finish":
      return {
        text: "Finish",
        class: "btn btn-primary float-right btn-stepper stepper-nav-btn",
      };
    // case "skip":
    //   return {
    //     text: "skip",
    //     class:
    //       "btn btn-outline-primary float-right btn-stepper stepper-nav-btn",
    //   };
    case "next":
      return {
        text: "Next",
        class: "btn btn-primary float-right btn-stepper stepper-nav-btn",
      };
    case "none":
    default:
      return {
        text: "",
        class: "btn-stepper stepper-nav-btn d-none",
      };
  }
}

function checkRequiredUserConfig(loginUser) {
  if (!loginUser) return;
  if (!loginUser.emailVerified) return "verify";
  if (loginUser.needsStripeOnboarding) return "onboarding";
  if (loginUser.needsStripeFinancialConnection) return "externalAccount";
  // TODO #agreements - should actually be using only userLevelRequiredIncomplete for the agreements step conditional but its currently broken until webhooks are fixed for bulk agreements
  // if (loginUser.__agreementMetrics?.userLevelRequiredIncomplete)
  //   return "agreements";
  if (
    loginUser.__agreementMetrics?.userLevelRequiredIncomplete
    && !loginUser.hasSignedCreatorAgreement
  ) {
    return "agreements";
  }
  // if (!loginUser.phone) return "phone";
  return false;
}

const mapStateToProps = (state) => {
  return {
    loading: state.settings.loading,
    loginUser: state.login.loginUser,
    stripeOnboardingUrl: state.settings.stripeOnboardingUrl,
    externalAccount: state.settings.externalAccount,
    stripeFinancialToken: state.settings.stripeFinancialToken,
    shouldUpdateUser: state.settings.shouldUpdateUser,
    showConfirmation: state.settings.showConfirmation,
  };
};
export default connect(mapStateToProps, {
  refreshStripeOnboardingUrl,
  requestAuthLink,
  addExternalAccount,
  readExternalAccount,
  generateFinancialConnectionToken,
  setLoginUserFromCookie,
  updatePayoutUser,
})(AccountCompleteSetup);
